import React, { useState, useEffect, useRef } from 'react';
import { axiosInstance, API } from '../App';
import { toast } from 'sonner';
import { Plus, Trash2, X, Edit2, Download, ShoppingCart, Upload, FileText, Eye } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertArabic } from 'arabic-reshaper';
import { NotoSansArabicBase64 } from '../fonts/NotoSansArabic';

// Helper function to process Arabic text for proper display in PDF
const processArabicText = (text) => {
  if (!text) return '';
  const arabicRegex = /[\u0600-\u06FF]/;
  if (!arabicRegex.test(text)) return text;
  return convertArabic(text);
};

const PurchaseOrders = ({ user }) => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [vendorQuotations, setVendorQuotations] = useState([]);   // P0 — VQ import in New PO
  const [selectedQuoteId, setSelectedQuoteId] = useState('');     // currently-picked Quote No.
  const [selectedQuoteLines, setSelectedQuoteLines] = useState({}); // {lineIdx: true}
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Check if user can delete a purchase order
  const canDelete = (po) => {
    if (user?.role === 'admin') return true;
    return po.status === 'draft';
  };
  
  const [formData, setFormData] = useState({
    supplier_id: '',
    supplier_name: '',
    supplier_name_arabic: '',
    po_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0, total: 0 }],
    subtotal: 0,
    vat_rate: 15,
    vat_amount: 0,
    total: 0,
    notes: '',
    vendor_quote_id: null,
    vendor_quote_no: null,
    delivery_time_days: '',
    payment_terms: ''
  });

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchData();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [posRes, suppliersRes, vqsRes] = await Promise.all([
        axiosInstance.get('/purchase-orders'),
        axiosInstance.get('/suppliers'),
        axiosInstance.get('/vendor-quotations')
      ]);
      setPurchaseOrders(posRes.data);
      setSuppliers(suppliersRes.data);
      const vqs = vqsRes.data?.quotations || vqsRes.data || [];
      // Only show the latest revision of each quote in the dropdown.
      setVendorQuotations(Array.isArray(vqs) ? vqs.filter(q => q?.is_latest !== false) : []);
    } catch (error) {
      if (loading) {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── P0 — Vendor Quotation → Purchase Order import helpers ───────────
  // Filter VQs to the chosen supplier; we never let users import a quote
  // from a different supplier than the one on the PO header.
  const supplierQuotes = vendorQuotations.filter(
    q => q.supplier_id === formData.supplier_id
  );

  const selectedQuote = supplierQuotes.find(q => q.id === selectedQuoteId);

  const allQuoteLinesSelected = selectedQuote
    && (selectedQuote.lines || []).length > 0
    && (selectedQuote.lines || []).every((_, i) => selectedQuoteLines[i]);

  const toggleAllQuoteLines = () => {
    if (!selectedQuote) return;
    if (allQuoteLinesSelected) {
      setSelectedQuoteLines({});
    } else {
      const next = {};
      (selectedQuote.lines || []).forEach((_, i) => { next[i] = true; });
      setSelectedQuoteLines(next);
    }
  };

  const recomputeTotals = (items, vatRate) => {
    const subtotal = items.reduce((s, it) => s + (Number(it.total) || 0), 0);
    const vatAmount = subtotal * ((vatRate ?? formData.vat_rate) / 100);
    return { subtotal, vat_amount: vatAmount, total: subtotal + vatAmount };
  };

  // Convert a 9-bucket payment_terms_breakdown into a readable one-liner.
  // Example: { advance_payment: 40, after_delivery: 30, after_installation: 20, retention: 10 }
  //   → "40% Advance · 30% After Delivery · 20% After Install · 10% Retention"
  const formatPaymentTermsBreakdown = (br) => {
    if (!br || typeof br !== 'object') return '';
    const labels = {
      advance_payment:            'Advance',
      design_engineering:         'Design',
      before_delivery:            'Before Delivery',
      after_delivery:             'After Delivery',
      after_installation:         'After Install',
      after_testing_commissioning:'After T&C',
      retention:                  'Retention'
    };
    return Object.entries(br)
      .filter(([k, v]) => v !== '' && v != null && Number(v) > 0)
      .map(([k, v]) => `${Number(v)}% ${labels[k] || k}`)
      .join(' · ');
  };

  const importQuoteLines = () => {
    if (!selectedQuote) {
      toast.error('Please select a Quote Vendor No. first');
      return;
    }
    const picked = (selectedQuote.lines || []).filter((_, i) => selectedQuoteLines[i]);
    if (picked.length === 0) {
      toast.error('Select at least one item from the quotation');
      return;
    }
    // Map VQ line shape → PO item shape; preserve traceability via
    // supplier_quotation_id on each item so the audit chain VQ→PO survives.
    const newItems = picked.map(ln => ({
      description: ln.supplier_description || ln.description || '',
      quantity: Number(ln.quantity) || 1,
      unit: ln.uom || 'unit',
      unit_price: Number(ln.unit_price) || 0,
      total: Number(ln.total) || (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0),
      item_code: ln.item_code || null,
      supplier_quotation_id: selectedQuote.id
    }));
    // Replace the empty starter row when present; otherwise append.
    const existing = formData.items;
    const isEmptyStarter = existing.length === 1
      && !existing[0].description && !existing[0].unit_price;
    const merged = isEmptyStarter ? newItems : [...existing, ...newItems];
    const totals = recomputeTotals(merged, formData.vat_rate);

    // ── Auto-fill commercial terms from the quotation ──────────────────
    // Delivery time = the longest line lead time (so the PO can promise no
    // earlier than the slowest material). Editable afterwards.
    const leadDays = (selectedQuote.lines || [])
      .map(l => Number(l?.lead_time_days))
      .filter(n => Number.isFinite(n) && n > 0);
    const computedLeadTime = leadDays.length ? Math.max(...leadDays) : '';
    // Payment terms = the quote's free-text terms, OR a one-liner summary
    // built from the 9-bucket breakdown when only the breakdown is set.
    const quoteTerms = selectedQuote.payment_terms
      || formatPaymentTermsBreakdown(selectedQuote.payment_terms_breakdown);
    // Only overwrite the existing values when this is a *fresh* import
    // (user hasn't already typed something there) — keeps manual edits safe.
    const nextDeliveryTime = formData.delivery_time_days
      ? formData.delivery_time_days
      : computedLeadTime;
    const nextPaymentTerms = formData.payment_terms
      ? formData.payment_terms
      : quoteTerms;
    // Also propagate to `expected_delivery_date` when empty.
    const nextExpectedDate = formData.expected_delivery_date
      || (computedLeadTime && formData.po_date
          ? new Date(new Date(formData.po_date).getTime()
                      + (computedLeadTime * 86400000))
              .toISOString().split('T')[0]
          : formData.expected_delivery_date);

    setFormData({
      ...formData,
      items: merged,
      ...totals,
      vendor_quote_id: selectedQuote.id,
      vendor_quote_no: selectedQuote.quote_no,
      delivery_time_days: nextDeliveryTime,
      payment_terms: nextPaymentTerms,
      expected_delivery_date: nextExpectedDate
    });
    toast.success(`Imported ${newItems.length} item(s) from ${selectedQuote.quote_no}`);
    setSelectedQuoteLines({});
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      setFormData({
        ...formData,
        supplier_id: supplierId,
        supplier_name: supplier.name_english,
        supplier_name_arabic: supplier.name_arabic,
        // Picking a different supplier invalidates any previously-imported
        // quote — clear lineage so users don't accidentally save a PO with
        // a supplier_quote_id from the wrong vendor.
        vendor_quote_id: null,
        vendor_quote_no: null
      });
    }
    setSelectedQuoteId('');
    setSelectedQuoteLines({});
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    const subtotal = newItems.reduce((sum, item) => sum + item.total, 0);
    const vatAmount = subtotal * (formData.vat_rate / 100);
    
    setFormData({
      ...formData,
      items: newItems,
      subtotal,
      vat_amount: vatAmount,
      total: subtotal + vatAmount
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit: 'unit', unit_price: 0, total: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      const subtotal = newItems.reduce((sum, item) => sum + item.total, 0);
      const vatAmount = subtotal * (formData.vat_rate / 100);
      
      setFormData({
        ...formData,
        items: newItems,
        subtotal,
        vat_amount: vatAmount,
        total: subtotal + vatAmount
      });
    }
  };

  const handleVatRateChange = (rate) => {
    const vatAmount = formData.subtotal * (rate / 100);
    setFormData({
      ...formData,
      vat_rate: rate,
      vat_amount: vatAmount,
      total: formData.subtotal + vatAmount
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_id || formData.items.length === 0) {
      toast.error('Please select a supplier and add items');
      return;
    }

    try {
      let poId;
      if (editingPO) {
        await axiosInstance.put(`/purchase-orders/${editingPO.id}`, formData);
        poId = editingPO.id;
        toast.success('Purchase Order updated successfully');
      } else {
        const response = await axiosInstance.post('/purchase-orders', formData);
        poId = response.data.id;
        if (response.data.approval_status === 'pending') {
          toast.success('Purchase Order submitted for approval', {
            description: 'Waiting for admin approval before it appears in the list'
          });
        } else {
          toast.success('Purchase Order created successfully');
        }
      }
      
      // Upload attachment if selected
      if (selectedFile && poId) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', selectedFile);
        try {
          await axiosInstance.post(`/purchase-orders/${poId}/upload-attachment`, uploadFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Attachment uploaded');
        } catch (uploadError) {
          toast.error('PO created but attachment upload failed');
        }
      }
      
      setShowModal(false);
      resetForm();
      setSelectedFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Purchase Order');
    }
  };

  const handleEdit = (po) => {
    setEditingPO(po);
    setFormData({
      supplier_id: po.supplier_id,
      supplier_name: po.supplier_name,
      supplier_name_arabic: po.supplier_name_arabic || '',
      po_date: po.po_date,
      expected_delivery_date: po.expected_delivery_date || '',
      items: po.items,
      subtotal: po.subtotal,
      vat_rate: po.vat_rate,
      vat_amount: po.vat_amount,
      total: po.total,
      notes: po.notes || '',
      vendor_quote_id: po.vendor_quote_id || null,
      vendor_quote_no: po.vendor_quote_no || null,
      delivery_time_days: po.delivery_time_days ?? '',
      payment_terms: po.payment_terms || ''
    });
    setSelectedQuoteId('');
    setSelectedQuoteLines({});
    setShowModal(true);
  };

  const handleStatusChange = async (poId, newStatus) => {
    try {
      await axiosInstance.put(`/purchase-orders/${poId}/status`, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this Purchase Order?')) {
      try {
        await axiosInstance.delete(`/purchase-orders/${id}`);
        toast.success('Purchase Order deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete Purchase Order');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '',
      supplier_name: '',
      supplier_name_arabic: '',
      po_date: new Date().toISOString().split('T')[0],
      expected_delivery_date: '',
      items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0, total: 0 }],
      subtotal: 0,
      vat_rate: 15,
      vat_amount: 0,
      total: 0,
      notes: '',
      vendor_quote_id: null,
      vendor_quote_no: null,
      delivery_time_days: '',
      payment_terms: ''
    });
    setEditingPO(null);
    setSelectedQuoteId('');
    setSelectedQuoteLines({});
  };

  const openNewModal = () => {
    resetForm();
    setShowModal(true);
  };

  const downloadPDF = async (po) => {
    const doc = new jsPDF();
    
    // Add Arabic font to jsPDF
    doc.addFileToVFS('NotoSansArabic-Regular.ttf', NotoSansArabicBase64);
    doc.addFont('NotoSansArabic-Regular.ttf', 'NotoSansArabic', 'normal');
    
    // Add Company Logo - same as Invoice
    try {
      const logoResponse = await fetch('/logo.png');
      if (logoResponse.ok) {
        const logoBlob = await logoResponse.blob();
        const logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });
        doc.addImage(logoBase64, 'PNG', 10, 5, 52, 30);
      }
    } catch (err) {
      console.error('Logo load failed:', err);
    }
    
    // Company Header - Arabic Name using Arabic font (right aligned)
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(10, 66, 117);
    doc.text(processArabicText('شركة الطريق العالي المتميز'), 190, 10, { align: 'right' });
    
    // English company name
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Distinct High Road', 190, 16, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Hafsa Bint Omar Street, AlRawdah Dist, Riyadh, KSA', 190, 22, { align: 'right' });
    doc.text('VAT: 314477769800003 | CR: 7052942476', 190, 27, { align: 'right' });
    doc.text('Tel: +966552168158 | Email: Info@dhr-sa.com', 190, 32, { align: 'right' });
    
    // Line separator
    doc.setDrawColor(10, 66, 117);
    doc.setLineWidth(0.5);
    doc.line(20, 38, 190, 38);

    // PO Title (centered)
    doc.setFontSize(18);
    doc.setTextColor(10, 66, 117);
    doc.setFont('helvetica', 'bold');
    doc.text('PURCHASE ORDER', 105, 48, { align: 'center' });
    doc.setFont('helvetica', 'normal');

    // Two columns: Supplier Details (Left) | PO Details (Right)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(10, 66, 117);
    doc.text('Supplier Details', 20, 58);
    doc.text('PO Details', 115, 58);

    // Supplier Details (Left Side)
    doc.setTextColor(0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    let supplierY = 66;
    doc.text(`Supplier: ${po.supplier_name}`, 20, supplierY);
    supplierY += 6;
    
    // Add Arabic supplier name if exists
    if (po.supplier_name_arabic) {
      doc.setFont('NotoSansArabic', 'normal');
      doc.text(processArabicText(po.supplier_name_arabic), 20, supplierY);
      doc.setFont('helvetica', 'normal');
      supplierY += 6;
    }

    // PO Details (Right Side)
    doc.setFontSize(9);
    let poY = 66;
    doc.text(`PO Number: ${po.po_number}`, 115, poY);
    poY += 6;
    doc.text(`PO Date: ${po.po_date}`, 115, poY);
    poY += 6;
    doc.text(`Expected Delivery: ${po.expected_delivery_date || 'N/A'}`, 115, poY);
    poY += 6;
    if (po.vendor_quote_no) {
      doc.setTextColor(10, 66, 117);
      doc.text(`Source Quote: ${po.vendor_quote_no}`, 115, poY);
      doc.setTextColor(0);
      poY += 6;
    }
    
    // Status (on the right header — guaranteed above the items table)
    const statusColors = {
      draft: [100, 100, 100],
      sent: [59, 130, 246],
      confirmed: [34, 197, 94],
      received: [16, 185, 129],
      cancelled: [239, 68, 68]
    };
    doc.setTextColor(...(statusColors[po.status] || [100, 100, 100]));
    doc.text(`Status: ${po.status?.toUpperCase() || 'DRAFT'}`, 115, poY);
    doc.setTextColor(0);
    poY += 6;

    // Items Table — start below whichever header section is taller (left
    // supplier block at ~72mm vs right PO block at poY).
    const tableStartY = Math.max(95, poY + 2);
    // Items Table with Unit column
    const tableData = po.items.map((item, index) => [
      index + 1,
      item.description,
      item.quantity.toString(),
      item.unit || 'unit',
      `${item.unit_price.toFixed(2)} SAR`,
      `${item.total.toFixed(2)} SAR`
    ]);

    autoTable(doc, {
      startY: tableStartY,
      head: [['#', 'Description', 'Qty', 'Unit', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [10, 66, 117],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      bodyStyles: {
        fontSize: 9
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 65 },
        2: { cellWidth: 15, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' },
        4: { cellWidth: 30, halign: 'right' },
        5: { cellWidth: 30, halign: 'right' }
      },
      margin: { left: 14, right: 14 }
    });

    // Totals
    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 150;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Subtotal: ${po.subtotal.toFixed(2)} SAR`, 140, finalY);
    doc.text(`VAT (${po.vat_rate}%): ${po.vat_amount.toFixed(2)} SAR`, 140, finalY + 6);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(10, 66, 117);
    doc.text(`Total: ${po.total.toFixed(2)} SAR`, 140, finalY + 14);

    // ── Payment Terms + Delivery Time (RIGHT side, directly under Total) ──
    // User-mandated layout (June 2026): 3-line block beneath the Total.
    //   Line 1: "Payment Terms:"  (label, bold)
    //   Line 2: wrapped terms text (regular)
    //   Line 3: "Delivery Time: N day(s)"
    let commercialBlockBottom = finalY + 14;
    if (po.payment_terms || (po.delivery_time_days != null && po.delivery_time_days !== '')) {
      let cy = finalY + 24;
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      if (po.payment_terms) {
        doc.setFont('helvetica', 'bold');
        doc.text('Payment Terms:', 140, cy);
        cy += 5;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(40, 40, 40);
        // Wrap inside the right column (~55mm wide so it stays aligned
        // with the totals strip and never bleeds into the left margin).
        const wrapped = doc.splitTextToSize(String(po.payment_terms), 55);
        doc.text(wrapped, 140, cy);
        cy += wrapped.length * 4.5;
      }
      if (po.delivery_time_days != null && po.delivery_time_days !== '') {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(80, 80, 80);
        doc.text('Delivery Time:', 140, cy + 1);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(40, 40, 40);
        doc.text(`${po.delivery_time_days} day(s)`, 167, cy + 1);
        cy += 5;
      }
      commercialBlockBottom = cy;
    }
    doc.setTextColor(0);
    doc.setFont('helvetica', 'normal');

    // Notes anchor — sits below totals + the commercial block.
    const notesAnchorY = Math.max(commercialBlockBottom + 8, finalY + 25);

    // Notes
    if (po.notes) {
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text('Notes:', 14, notesAnchorY);
      const notesText = po.notes;
      if (/[\u0600-\u06FF]/.test(notesText)) {
        doc.setFont('NotoSansArabic', 'normal');
        doc.text(processArabicText(notesText), 14, notesAnchorY + 6);
      } else {
        doc.text(notesText, 14, notesAnchorY + 6);
      }
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'normal');
    doc.text('This is a computer-generated document.', 105, 285, { align: 'center' });

    // Download PDF directly
    doc.save(`PurchaseOrder-${po.po_number}.pdf`);
    toast.success('PDF downloaded');
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      confirmed: 'bg-green-100 text-green-700',
      received: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Stats
  const stats = {
    total: purchaseOrders.length,
    draft: purchaseOrders.filter(po => po.status === 'draft').length,
    confirmed: purchaseOrders.filter(po => po.status === 'confirmed').length,
    totalAmount: purchaseOrders.reduce((sum, po) => sum + po.total, 0)
  };

  if (loading) {
    return <div className="text-center py-12">Loading purchase orders...</div>;
  }

  return (
    <div data-testid="purchase-orders-page">
      <div className="page-header">
        <h2 className="page-title">Purchase Orders</h2>
        <button
          data-testid="add-po-button"
          onClick={openNewModal}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New Purchase Order
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card">
          <div className="stat-icon blue">
            <ShoppingCart className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total POs</h3>
          <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon yellow">
            <ShoppingCart className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Draft</h3>
          <p className="text-3xl font-bold text-yellow-600">{stats.draft}</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <ShoppingCart className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Confirmed</h3>
          <p className="text-3xl font-bold text-green-600">{stats.confirmed}</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <ShoppingCart className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Amount</h3>
          <p className="text-2xl font-bold text-purple-600">{stats.totalAmount.toLocaleString()} SAR</p>
        </div>
      </div>

      {/* Purchase Orders Table - Split View */}
      <div className="flex gap-4">
        <div className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 ${selectedPO ? 'w-1/2' : 'w-full'}`}>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">PO Number</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Supplier</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {purchaseOrders.length > 0 ? (
                purchaseOrders.map((po) => (
                  <tr 
                    key={po.id} 
                    data-testid={`po-row-${po.id}`}
                    onClick={() => setSelectedPO(selectedPO?.id === po.id ? null : po)}
                    className={`cursor-pointer hover:bg-blue-50 transition-colors ${selectedPO?.id === po.id ? 'bg-blue-100 border-l-4 border-blue-500' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <span className="font-semibold text-blue-600">{po.po_number}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{po.supplier_name}</p>
                        {po.supplier_name_arabic && (
                          <p className="text-sm text-gray-500" dir="rtl">{po.supplier_name_arabic}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{po.po_date}</td>
                    <td className="px-4 py-3 text-right font-semibold">{po.total.toLocaleString()} SAR</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(po.status)}`}>
                        {po.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => downloadPDF(po)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(po)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {canDelete(po) && (
                          <button
                            onClick={() => handleDelete(po.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center text-gray-500">
                    No purchase orders found. Create your first PO!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PO Details Panel */}
        {selectedPO && (
          <div className="w-1/2 bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedPO.po_number}</h3>
                <p className="text-sm text-gray-500">Purchase Order Details</p>
              </div>
              <button 
                onClick={() => setSelectedPO(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Status & Actions */}
            <div className="flex items-center gap-3 mb-6">
              <select
                value={selectedPO.status}
                onChange={(e) => {
                  handleStatusChange(selectedPO.id, e.target.value);
                  setSelectedPO({...selectedPO, status: e.target.value});
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border-0 cursor-pointer ${getStatusColor(selectedPO.status)}`}
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="confirmed">Confirmed</option>
                <option value="received">Received</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button
                onClick={() => downloadPDF(selectedPO)}
                className="btn-primary text-sm py-1 px-3"
              >
                <Download className="w-4 h-4 inline mr-1" />
                PDF
              </button>
            </div>

            {/* Supplier Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Supplier Information</h4>
              <p className="font-medium">{selectedPO.supplier_name}</p>
              {selectedPO.supplier_name_arabic && (
                <p className="text-gray-600" dir="rtl">{selectedPO.supplier_name_arabic}</p>
              )}
            </div>

            {/* PO Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">PO Information</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">PO Date:</span>
                  <p className="font-medium">{selectedPO.po_date}</p>
                </div>
                <div>
                  <span className="text-gray-500">Expected Delivery:</span>
                  <p className="font-medium">{selectedPO.expected_delivery_date || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Delivery Time (days):</span>
                  <p className="font-medium" data-testid="po-detail-delivery-time">
                    {selectedPO.delivery_time_days != null && selectedPO.delivery_time_days !== ''
                      ? `${selectedPO.delivery_time_days} day(s)`
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Payment Terms:</span>
                  <p className="font-medium whitespace-pre-line" data-testid="po-detail-payment-terms">
                    {selectedPO.payment_terms || 'N/A'}
                  </p>
                </div>
                {selectedPO.vendor_quote_no && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Source Quote:</span>
                    <p className="font-medium text-blue-700">
                      Linked → {selectedPO.vendor_quote_no}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Items */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Items</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-center">Qty</th>
                      <th className="px-3 py-2 text-center">Unit</th>
                      <th className="px-3 py-2 text-right">Price</th>
                      <th className="px-3 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPO.items?.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">{item.description}</td>
                        <td className="px-3 py-2 text-center">{item.quantity}</td>
                        <td className="px-3 py-2 text-center">{item.unit || 'unit'}</td>
                        <td className="px-3 py-2 text-right">{item.unit_price?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-medium">{item.total?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex justify-between text-sm mb-1">
                <span>Subtotal:</span>
                <span>{selectedPO.subtotal?.toLocaleString()} SAR</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span>VAT ({selectedPO.vat_rate || 15}%):</span>
                <span>{selectedPO.vat_amount?.toLocaleString()} SAR</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                <span>Total:</span>
                <span className="text-blue-600">{selectedPO.total?.toLocaleString()} SAR</span>
              </div>
            </div>

            {/* Notes */}
            {selectedPO.notes && (
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Notes</h4>
                <p className="text-sm text-gray-600">{selectedPO.notes}</p>
              </div>
            )}

            {/* Attachment */}
            {selectedPO.attachment_url && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">Attachment</h4>
                {selectedPO.attachment_url.toLowerCase().endsWith('.pdf') ? (
                  <div className="flex items-center gap-3 bg-red-50 p-3 rounded-lg border">
                    <FileText className="w-10 h-10 text-red-600" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">PDF Document</p>
                      <p className="text-xs text-gray-500">Purchase Order attachment</p>
                    </div>
                    <a
                      href={`${API.replace('/api', '')}${selectedPO.attachment_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 hover:bg-red-700"
                    >
                      <Eye className="w-4 h-4" /> View PDF
                    </a>
                  </div>
                ) : (
                  <div className="relative">
                    <img 
                      src={`${API.replace('/api', '')}${selectedPO.attachment_url}`}
                      alt="PO attachment"
                      className="w-full h-40 object-cover rounded-lg"
                    />
                    <a
                      href={`${API.replace('/api', '')}${selectedPO.attachment_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" /> View Full
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content max-w-6xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingPO ? `Edit ${editingPO.po_number}` : 'New Purchase Order'}
              </h3>
              <button onClick={() => setShowModal(false)} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Supplier & Dates */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Supplier *</label>
                  <select
                    data-testid="po-supplier-select"
                    value={formData.supplier_id}
                    onChange={(e) => handleSupplierChange(e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="">Select Supplier</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name_english}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">PO Date *</label>
                  <input
                    type="date"
                    value={formData.po_date}
                    onChange={(e) => setFormData({ ...formData, po_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Expected Delivery Date</label>
                  <input
                    type="date"
                    value={formData.expected_delivery_date}
                    onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>

              {/* ── Vendor Quotation import (NEW) ─────────────────────────
                 When a supplier has live quotations, the user can pick the
                 winning quote and pull its lines straight into the PO. The
                 dropdown is intentionally hidden in edit mode to keep the
                 audit chain clean (old POs don't get retroactively linked). */}
              {!editingPO && (
                <div className="mb-4 p-4 rounded-lg border border-blue-200 bg-blue-50/50">
                  <div className="flex items-center justify-between mb-3">
                    <label className="form-label !mb-0 text-blue-900 font-semibold">
                      Quote Vendor No. (Import items from Vendor Quotation)
                    </label>
                    {formData.vendor_quote_no && (
                      <span
                        data-testid="po-vq-lineage-pill"
                        className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium"
                      >
                        Linked → {formData.vendor_quote_no}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                    <div className="md:col-span-7">
                      <select
                        data-testid="po-vendor-quote-select"
                        value={selectedQuoteId}
                        onChange={(e) => {
                          setSelectedQuoteId(e.target.value);
                          setSelectedQuoteLines({});
                        }}
                        disabled={!formData.supplier_id}
                        className="form-input"
                      >
                        <option value="">
                          {!formData.supplier_id
                            ? '— Pick a supplier first —'
                            : supplierQuotes.length === 0
                              ? '— No quotations for this supplier —'
                              : 'Select a Quote No. …'}
                        </option>
                        {supplierQuotes.map(q => (
                          <option key={q.id} value={q.id}>
                            {q.quote_no}{q.supplier_quote_no ? ` · Vendor Ref: ${q.supplier_quote_no}` : ''}
                            {q.customer_rfq_no ? ` · RFQ: ${q.customer_rfq_no}` : ''}
                            {` · ${(q.lines || []).length} item(s)`}
                            {q.grand_total != null ? ` · ${Number(q.grand_total).toFixed(2)} ${q.currency || 'SAR'}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-5 flex gap-2 justify-end">
                      <button
                        type="button"
                        data-testid="po-vq-import-btn"
                        onClick={importQuoteLines}
                        disabled={!selectedQuote
                          || Object.values(selectedQuoteLines).every(v => !v)}
                        className="btn-primary text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Add selected to PO →
                      </button>
                    </div>
                  </div>

                  {/* Items preview table (Supplier Description | Qty | UOM | Unit Price | Total) */}
                  {selectedQuote && (selectedQuote.lines || []).length > 0 && (
                    <div className="mt-3 bg-white rounded-md border border-blue-100 overflow-hidden">
                      <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-blue-50 sticky top-0">
                            <tr className="text-blue-900">
                              <th className="px-2 py-2 text-left w-10">
                                <input
                                  data-testid="po-vq-select-all"
                                  type="checkbox"
                                  checked={!!allQuoteLinesSelected}
                                  onChange={toggleAllQuoteLines}
                                  className="cursor-pointer"
                                />
                              </th>
                              <th className="px-2 py-2 text-left">Supplier Description</th>
                              <th className="px-2 py-2 text-right w-20">Qty</th>
                              <th className="px-2 py-2 text-center w-20">UOM</th>
                              <th className="px-2 py-2 text-right w-24">Unit Price</th>
                              <th className="px-2 py-2 text-right w-28">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedQuote.lines || []).map((ln, i) => (
                              <tr
                                key={ln.id || i}
                                className={`border-t border-blue-50 hover:bg-blue-50/40 ${selectedQuoteLines[i] ? 'bg-blue-50/70' : ''}`}
                              >
                                <td className="px-2 py-2">
                                  <input
                                    data-testid={`po-vq-line-${i}-checkbox`}
                                    type="checkbox"
                                    checked={!!selectedQuoteLines[i]}
                                    onChange={() => setSelectedQuoteLines({
                                      ...selectedQuoteLines, [i]: !selectedQuoteLines[i]
                                    })}
                                    className="cursor-pointer"
                                  />
                                </td>
                                <td className="px-2 py-2 text-gray-800">
                                  {ln.supplier_description || ln.description || '—'}
                                </td>
                                <td className="px-2 py-2 text-right">{ln.quantity ?? '—'}</td>
                                <td className="px-2 py-2 text-center">{ln.uom || '—'}</td>
                                <td className="px-2 py-2 text-right">
                                  {ln.unit_price != null ? Number(ln.unit_price).toFixed(2) : '—'}
                                </td>
                                <td className="px-2 py-2 text-right font-semibold">
                                  {ln.total != null ? Number(ln.total).toFixed(2) : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="px-3 py-2 bg-gray-50 text-xs text-gray-600 flex justify-between">
                        <span>{Object.values(selectedQuoteLines).filter(Boolean).length} of {(selectedQuote.lines || []).length} selected</span>
                        <span>You can still edit qty / price / unit after import.</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Commercial Terms (auto-filled from imported VQ; editable) ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label flex items-center gap-2">
                    Delivery Time (days)
                    {formData.vendor_quote_no && (
                      <span className="text-[10px] uppercase tracking-wider text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                        from {formData.vendor_quote_no}
                      </span>
                    )}
                  </label>
                  <input
                    data-testid="po-delivery-time-days"
                    type="number"
                    min="0"
                    value={formData.delivery_time_days}
                    onChange={(e) => setFormData({
                      ...formData,
                      delivery_time_days: e.target.value === '' ? '' : Number(e.target.value)
                    })}
                    placeholder="e.g. 14"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label flex items-center gap-2">
                    Payment Terms
                    {formData.vendor_quote_no && (
                      <span className="text-[10px] uppercase tracking-wider text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                        from {formData.vendor_quote_no}
                      </span>
                    )}
                  </label>
                  <input
                    data-testid="po-payment-terms"
                    type="text"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    placeholder="e.g. Net 30 — or 40% Advance · 60% After Delivery"
                    className="form-input"
                  />
                </div>
              </div>

              {/* Items */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="form-label">Items</label>
                  <button
                    type="button"
                    onClick={addItem}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    + Add Item
                  </button>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-4">
                        <textarea
                          placeholder="Description (multi-line — Shift+Enter for new line)"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          rows={3}
                          className="form-input text-sm leading-snug resize-y min-h-[68px]"
                          required
                        />
                      </div>
                      <div className="col-span-1">
                        <input
                          type="number"
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="form-input text-sm text-center text-lg font-semibold"
                          min="1"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <select
                          value={item.unit || 'unit'}
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                          className="form-input text-sm"
                        >
                          <option value="unit">Unit</option>
                          <option value="pcs">Pcs</option>
                          <option value="kg">Kg</option>
                          <option value="m">Meter</option>
                          <option value="m2">Sq.M</option>
                          <option value="m3">Cu.M</option>
                          <option value="liter">Liter</option>
                          <option value="box">Box</option>
                          <option value="set">Set</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          placeholder="Unit Price"
                          value={item.unit_price}
                          onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="form-input text-sm"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="text"
                          value={`${item.total.toFixed(2)} SAR`}
                          className="form-input text-sm bg-gray-100"
                          readOnly
                        />
                      </div>
                      <div className="col-span-1 flex justify-center pt-1">
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Attachment Upload */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="flex items-center justify-between mb-2">
                  <label className="form-label flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-500" />
                    Attachment (PDF/Image)
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*,.pdf,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        setSelectedFile(e.target.files[0]);
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                  >
                    <Upload className="w-4 h-4" />
                    Choose File
                  </button>
                </div>
                {selectedFile ? (
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg border">
                    <FileText className="w-8 h-8 text-blue-600" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-2">
                    No file selected. You can attach a PDF or image.
                  </p>
                )}
              </div>

              {/* Totals */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="form-input"
                    rows="3"
                    placeholder="Additional notes..."
                  />
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">{formData.subtotal.toFixed(2)} SAR</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">VAT:</span>
                      <div className="flex items-center gap-2">
                        <select
                          value={formData.vat_rate}
                          onChange={(e) => handleVatRateChange(parseFloat(e.target.value))}
                          className="form-input w-20 text-sm py-1"
                        >
                          <option value="0">0%</option>
                          <option value="5">5%</option>
                          <option value="15">15%</option>
                        </select>
                        <span className="font-medium">{formData.vat_amount.toFixed(2)} SAR</span>
                      </div>
                    </div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="font-bold text-gray-900">Total:</span>
                      <span className="font-bold text-blue-600 text-lg">{formData.total.toFixed(2)} SAR</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 -mx-4 px-4 pt-4 pb-2 bg-white/95 backdrop-blur border-t border-gray-200 flex gap-3 mt-6">
                <button
                  data-testid="submit-po-button"
                  type="submit"
                  className="btn-primary flex-1"
                >
                  {editingPO ? 'Update Purchase Order' : 'Create Purchase Order'}
                </button>
                <button
                  data-testid="cancel-po-button"
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary min-w-[140px]"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrders;
